import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, ButtonGroup, Col, Container, Input, InputGroup, InputGroupText, Row, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { utils, writeFileXLSX } from "xlsx";
import { useFacebookFormCore } from "../../cores/facebookForm";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import FormQuestions from "../component/forms/facebook/modal/FormQuestions";
import LeadCard from "../component/forms/facebook/LeadCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { FACEBOOK_FORM_LEADS_LOADING } from "../../constants/loading.constant";
import { useIntl } from "react-intl";
import { CONTACT_EXPAND_CREATION_FORM, FACEBOOK_VIEW_FORM } from "../../constants/modal.constant";
import { useGlobalCore } from "../../cores/globals";
import { NoData } from "../component/NoData";
import "./FacebookForms.scss";
import { contactApiService } from "../../cores/contacts/services/api";
import { ContactSource } from "../../cores/contacts/enum/ContactSource.enum";
import { useContactCore } from "../../cores/contacts";
import {
  buildContactPayloadFromLead,
  buildFacebookFormContactFilters,
  ensureUserIsContactOwner,
  extractLeadDataFromFormLead,
  contactHasOpportunityOnBoard,
  findExistingContactId,
  getContactApiErrorMessage,
  isDuplicateContactError,
  isDuplicateOpportunityError,
  linkFacebookLeadToContact,
} from "../component/forms/facebook/leadContact.utils";
import { useBoardCore } from "../../cores/boards";
import { CONTACT_EXPAND_EDIT_FORM } from "../../constants/modal.constant";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import EditContactForm from "../component/contacts/modal/EditContactForm";
import { FormLead } from "../../cores/facebookForm/interfaces/api/get-facebook-form";
import CreateContactForm from "../component/contacts/modal/CreateContactForm";
import WaitingGameOffer from "../component/games/WaitingGameOffer";
import SendLeadsToCrmModal from "../component/forms/facebook/SendLeadsToCrmModal";

type LeadViewMode = "grid" | "cards" | "table";

const BULK_CREATE_CONTACTS_TOAST_ID = "facebook-bulk-create-contacts";
const BULK_SEND_CRM_TOAST_ID = "facebook-bulk-send-crm";

export default function ViewFacebookForm() {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const { isDarkMode, turnOffModal, turnOnModal, getModalVisibility, isModal } = useGlobalCore();
  const { company, user, socialSettings, companyRole, isAdmin, isReseller } = useMeCore();
  const { facebookForm, getFacebookForm } = useFacebookFormCore();
  const { goToFacebookForms, goToSocial, history, routes } = useRouterCore();
  const { getContact, getContactTags, getCustomFields, getContacts, setContactFilters, setSource } =
    useContactCore();
  const { getBusinessBoards, postCard } = useBoardCore();
  const [viewMode, setViewMode] = useState<LeadViewMode>("grid");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
  const [isCreatingAllContacts, setIsCreatingAllContacts] = useState(false);
  const [isSendingToCrm, setIsSendingToCrm] = useState(false);
  const [showCrmModal, setShowCrmModal] = useState(false);
  const [bulkContactProgress, setBulkContactProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [bulkCrmProgress, setBulkCrmProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [creatingContactLeadId, setCreatingContactLeadId] = useState<string | null>(null);
  const [leadActionLoading, setLeadActionLoading] = useState(false);
  const isBulkProcessing = isCreatingAllContacts || isSendingToCrm;
  const bulkProgress = isSendingToCrm ? bulkCrmProgress : bulkContactProgress;
  const isWaitingForContacts =
    leadActionLoading || !!creatingContactLeadId || isBulkProcessing;

  useEffect(() => {
    if (
      !socialSettings?.facebook || (
        !companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller
      )
    ) goToSocial();
  }, [goToSocial, socialSettings, companyRole, isAdmin, isReseller]);

  const toggleSideModal = useCallback(() => {
    turnOnModal(FACEBOOK_VIEW_FORM, true);
  }, [turnOnModal]);

  useEffect(() => {
    getFacebookForm(company?.id!, id);
  }, [getFacebookForm, company, id]);

  const leads = useMemo(() => {
    if (!facebookForm?.leads) return [];
    const q = search.trim().toLowerCase();
    const withDate = facebookForm.leads.map((lead) => ({
      ...lead,
      createdAtMs: Date.parse(lead.createdTime || "") || 0,
    }));

    let next = withDate.filter((lead) => {
      if (!q) return true;
      const blob = `${lead.id} ${lead.createdTime} ${(lead.data || []).join(" ")}`.toLowerCase();
      return blob.includes(q);
    });

    next = [...next].sort((a, b) =>
      sortBy === "newest" ? b.createdAtMs - a.createdAtMs : a.createdAtMs - b.createdAtMs
    );
    return next;
  }, [facebookForm?.leads, search, sortBy]);

  const leadStats = useMemo(() => {
    const total = facebookForm?.leads?.length || 0;
    const questions = facebookForm?.form?.questions?.length || 0;
    return { total, questions };
  }, [facebookForm?.leads?.length, facebookForm?.form?.questions?.length]);

  const extractLeadData = useCallback(
    (lead: FormLead) =>
      extractLeadDataFromFormLead(lead, facebookForm?.form?.questions || []),
    [facebookForm?.form?.questions]
  );

  const openContactsModule = useCallback(() => {
    if (!company?.id) return;

    const isPrivilegedUser =
      !!isAdmin ||
      !!isReseller ||
      !!companyRole?.owner ||
      !!companyRole?.admin;
    const nextFilters = buildFacebookFormContactFilters(user?.id, isPrivilegedUser);

    setSource(ContactSource.FACEBOOK_FORM);
    setContactFilters(nextFilters);
    getContacts(company.id, nextFilters, false);
    history.push(
      `${routes.contacts}?source=${encodeURIComponent(ContactSource.FACEBOOK_FORM)}`
    );
  }, [
    company?.id,
    companyRole?.admin,
    companyRole?.owner,
    getContacts,
    history,
    isAdmin,
    isReseller,
    routes.contacts,
    setContactFilters,
    setSource,
    user?.id,
  ]);

  const getLeadPrimaryLabel = useCallback(
    (lead: FormLead) => {
      const { name, email, phone } = extractLeadData(lead);
      return {
        title: name || "Lead sin nombre",
        subtitle: email || phone || "Sin email ni teléfono",
      };
    },
    [extractLeadData]
  );

  const openContactEditor = useCallback(
    async (contactId: string) => {
      if (!company?.id) return;
      getContactTags(company.id);
      getCustomFields(company.id);
      getContact(company.id, contactId);
      getBusinessBoards(company.id);
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm onDelete={() => {}} onUpdate={() => {}} />,
        Position.LEFT
      );
    },
    [company?.id, getBusinessBoards, getContact, getContactTags, getCustomFields, turnOnModal]
  );

  const handleExistingContact = useCallback(
    async (
      existingContactId: string,
      contactLabel: string,
      lead?: FormLead
    ) => {
      if (!company?.id) return;

      const ownerResult = await ensureUserIsContactOwner(
        company.id,
        existingContactId,
        user?.id
      );

      if (ownerResult === "assigned") {
        toast.warning(
          intl.formatMessage(
            { id: "app.facebookForm.contactAlreadyExistsAssigned" },
            { name: contactLabel }
          ),
          { autoClose: 5000 }
        );
      } else {
        toast.warning(
          intl.formatMessage(
            { id: "app.facebookForm.contactAlreadyExists" },
            { name: contactLabel }
          ),
          { autoClose: 5000 }
        );
      }

      if (lead && id) {
        await linkFacebookLeadToContact(
          company.id,
          existingContactId,
          lead,
          id
        );
      }

      await openContactEditor(existingContactId);
    },
    [company?.id, id, intl, openContactEditor, user?.id]
  );

  const onOpenOrCreateContact = useCallback(
    async (lead: FormLead) => {
      if (!company?.id || creatingContactLeadId || isBulkProcessing) return;

      setCreatingContactLeadId(lead.id);
      const { name, email, phone } = extractLeadData(lead);
      const contactLabel = name || "Lead Facebook";
      const fallbackToCreateModal = () => {
        turnOnModal(
          CONTACT_EXPAND_CREATION_FORM,
          <CreateContactForm
            source={ContactSource.FACEBOOK_FORM}
            preContactData={{
              name: name || `Lead Facebook ${String(lead.id).slice(-6)}`,
              email: email || "",
              reference: `Facebook Form: ${facebookForm?.form?.name || ""}`.trim(),
            }}
          />,
          Position.LEFT
        );
      };

      try {
        const existingContactId = await findExistingContactId(
          company.id,
          email,
          phone
        );

        if (existingContactId) {
          await handleExistingContact(existingContactId, contactLabel, lead);
          return;
        }

        const payload = buildContactPayloadFromLead({
          lead,
          name,
          email,
          phone,
          formName: facebookForm?.form?.name || "",
          userId: user?.id,
        });

        const created = await contactApiService.postContact(company.id, payload);

        if (created?.id) {
          if (id) {
            await linkFacebookLeadToContact(company.id, created.id, lead, id);
          }
          toast.success(`Contacto creado: ${contactLabel}`);
          await openContactEditor(created.id);
          return;
        }

        fallbackToCreateModal();
      } catch (error) {
        console.error(error);

        if (isDuplicateContactError(error)) {
          const existingContactId = await findExistingContactId(
            company.id,
            email,
            phone
          );

          if (existingContactId) {
            await handleExistingContact(existingContactId, contactLabel, lead);
            return;
          }

          toast.warning(
            intl.formatMessage(
              { id: "app.facebookForm.contactAlreadyExists" },
              { name: contactLabel }
            ),
            { autoClose: 5000 }
          );
          return;
        }

        toast.error("No se pudo abrir ni crear el contacto. Intenta nuevamente.");
        fallbackToCreateModal();
      } finally {
        setCreatingContactLeadId(null);
      }
    },
    [
      company?.id,
      creatingContactLeadId,
      extractLeadData,
      facebookForm?.form?.name,
      handleExistingContact,
      intl,
      isBulkProcessing,
      openContactEditor,
      turnOnModal,
      user?.id,
    ]
  );

  const onCreateAllContacts = useCallback(async () => {
    if (!company?.id || !facebookForm?.leads?.length || isCreatingAllContacts) return;

    const isAgentView =
      !isAdmin &&
      !isReseller &&
      !companyRole?.owner &&
      !companyRole?.admin;

    if (isAgentView && !user?.id) {
      toast.error("No se pudo identificar tu usuario para asignar los contactos.");
      return;
    }

    const totalLeads = facebookForm.leads.length;
    setIsCreatingAllContacts(true);
    setBulkContactProgress({ current: 0, total: totalLeads });
    toast.info(
      `Creando contactos desde ${totalLeads} leads. Esto puede tardar unos momentos...`,
      { toastId: BULK_CREATE_CONTACTS_TOAST_ID, autoClose: false }
    );

    let created = 0;
    let assigned = 0;
    let skipped = 0;
    let failed = 0;
    let lastErrorMessage = "";
    const processedEmails = new Set<string>();
    const processedPhones = new Set<string>();

    try {
      for (let index = 0; index < facebookForm.leads.length; index += 1) {
        const lead = facebookForm.leads[index];
        setBulkContactProgress({ current: index + 1, total: totalLeads });

        const { name, email, phone } = extractLeadData(lead);
        const normalizedEmail = email.toLowerCase();
        const normalizedPhone = phone.trim();

        if (normalizedEmail && processedEmails.has(normalizedEmail)) {
          skipped += 1;
          continue;
        }
        if (!normalizedEmail && normalizedPhone && processedPhones.has(normalizedPhone)) {
          skipped += 1;
          continue;
        }

        try {
          const existingId = await findExistingContactId(company.id, email, phone);
          if (existingId) {
            const ownerResult = await ensureUserIsContactOwner(
              company.id,
              existingId,
              user?.id,
              false
            );
            if (ownerResult === "assigned") assigned += 1;
            else skipped += 1;

            if (id) {
              await linkFacebookLeadToContact(company.id, existingId, lead, id);
            }

            if (normalizedEmail) processedEmails.add(normalizedEmail);
            if (normalizedPhone) processedPhones.add(normalizedPhone);
            continue;
          }

          const payload = buildContactPayloadFromLead({
            lead,
            name,
            email,
            phone,
            formName: facebookForm?.form?.name || "",
            userId: user?.id,
            notifyOwners: false,
          });

          const createdContact = await contactApiService.postContact(company.id, payload);
          if (createdContact?.id) {
            if (id) {
              await linkFacebookLeadToContact(
                company.id,
                createdContact.id,
                lead,
                id
              );
            }
            created += 1;
            if (normalizedEmail) processedEmails.add(normalizedEmail);
            if (normalizedPhone) processedPhones.add(normalizedPhone);
          } else {
            failed += 1;
          }
        } catch (error) {
          console.error(error);
          failed += 1;
          lastErrorMessage = getContactApiErrorMessage(error);
        }
      }

      toast.dismiss(BULK_CREATE_CONTACTS_TOAST_ID);

      const ownerFilter =
        isAgentView && user?.id ? String(user.id) : "";

      let visibleInDatabase = 0;
      try {
        const verification = await contactApiService.getContacts(company.id, {
          page: 1,
          perPage: 1,
          sources: [ContactSource.FACEBOOK_FORM],
          owner: ownerFilter,
        });
        visibleInDatabase = verification.total || 0;
      } catch (error) {
        console.error("Bulk contacts verification failed", error);
      }

      const visibleCount = created + assigned;
      const summaryParts = [
        created > 0 ? `${created} creado(s)` : "",
        assigned > 0 ? `${assigned} asignado(s) a tu usuario` : "",
        skipped > 0 ? `${skipped} sin cambios` : "",
        failed > 0 ? `${failed} con error` : "",
      ].filter(Boolean);

      if (failed > 0 && visibleCount === 0) {
        toast.error(
          `No se pudieron procesar los contactos. ${summaryParts.join(". ")}.${
            lastErrorMessage ? ` Último error: ${lastErrorMessage}` : ""
          }`
        );
      } else if (visibleCount > 0) {
        try {
          await contactApiService.notifyBulkAssignment(company.id, {
            count: visibleCount,
            formName: facebookForm?.form?.name || "Facebook Form",
            source: ContactSource.FACEBOOK_FORM,
          });
        } catch (error) {
          console.error("Bulk assignment summary email failed", error);
        }

        const verificationHint =
          visibleInDatabase > 0
            ? ` Verificados ${visibleInDatabase} en base (origen Facebook Form).`
            : " No encontramos contactos visibles con el filtro actual. Revisa asignación y origen en Contactos.";

        toast.success(
          `Listo: ${summaryParts.join(". ")}.${verificationHint} Haz clic aquí para abrir Contactos.`,
          {
            onClick: () =>
              history.push(
                `${routes.contacts}?source=${encodeURIComponent(ContactSource.FACEBOOK_FORM)}`
              ),
          }
        );
      } else if (skipped > 0) {
        toast.info(
          `Proceso finalizado. ${skipped} contacto(s) ya existían y ya estaban asignados a tu usuario.${
            visibleInDatabase > 0
              ? ` Verificados ${visibleInDatabase} visibles en Contactos.`
              : ""
          }`
        );
      } else {
        toast.warning("No se procesó ningún contacto.");
      }
    } catch (error) {
      console.error(error);
      toast.dismiss(BULK_CREATE_CONTACTS_TOAST_ID);
      toast.error("No se pudo completar la creación masiva de contactos.");
    } finally {
      setIsCreatingAllContacts(false);
      setBulkContactProgress(null);
    }
  }, [
    company?.id,
    companyRole,
    extractLeadData,
    facebookForm?.form?.name,
    facebookForm?.leads,
    findExistingContactId,
    isAdmin,
    isCreatingAllContacts,
    isReseller,
    openContactsModule,
    user?.id,
  ]);

  const onSendLeadsToCrm = useCallback(
    async (boardId: string, listId: string) => {
      if (!company?.id || !facebookForm?.leads?.length || isSendingToCrm) return;

      const isAgentView =
        !isAdmin &&
        !isReseller &&
        !companyRole?.owner &&
        !companyRole?.admin;

      if (isAgentView && !user?.id) {
        toast.error("No se pudo identificar tu usuario para asignar los contactos.");
        return;
      }

      setShowCrmModal(false);
      const totalLeads = facebookForm.leads.length;
      setIsSendingToCrm(true);
      setBulkCrmProgress({ current: 0, total: totalLeads });
      toast.info(
        `Enviando ${totalLeads} leads al CRM. Esto puede tardar unos momentos...`,
        { toastId: BULK_SEND_CRM_TOAST_ID, autoClose: false }
      );

      let contactsCreated = 0;
      let contactsAssigned = 0;
      let contactsSkipped = 0;
      let opportunitiesCreated = 0;
      let opportunitiesSkipped = 0;
      let failed = 0;
      let lastErrorMessage = "";
      const processedEmails = new Set<string>();
      const processedPhones = new Set<string>();
      const boardOpportunityCache = new Map<string, boolean>();

      try {
        for (let index = 0; index < facebookForm.leads.length; index += 1) {
          const lead = facebookForm.leads[index];
          setBulkCrmProgress({ current: index + 1, total: totalLeads });

          const { name, email, phone } = extractLeadData(lead);
          const contactLabel = name || "Lead Facebook";
          const normalizedEmail = email.toLowerCase();
          const normalizedPhone = phone.trim();

          if (normalizedEmail && processedEmails.has(normalizedEmail)) {
            contactsSkipped += 1;
            continue;
          }
          if (!normalizedEmail && normalizedPhone && processedPhones.has(normalizedPhone)) {
            contactsSkipped += 1;
            continue;
          }

          try {
            let contactId = await findExistingContactId(company.id, email, phone);

            if (contactId) {
              const ownerResult = await ensureUserIsContactOwner(
                company.id,
                contactId,
                user?.id,
                false
              );
              if (ownerResult === "assigned") contactsAssigned += 1;
              else contactsSkipped += 1;
            } else {
              const payload = buildContactPayloadFromLead({
                lead,
                name,
                email,
                phone,
                formName: facebookForm?.form?.name || "",
                userId: user?.id,
                notifyOwners: false,
              });

              const createdContact = await contactApiService.postContact(company.id, payload);
              if (!createdContact?.id) {
                failed += 1;
                continue;
              }

              contactId = createdContact.id;
              contactsCreated += 1;
            }

            if (contactId && id) {
              await linkFacebookLeadToContact(company.id, contactId, lead, id);
            }

            if (normalizedEmail) processedEmails.add(normalizedEmail);
            if (normalizedPhone) processedPhones.add(normalizedPhone);

            const hasExistingOpportunity = await contactHasOpportunityOnBoard(
              company.id,
              contactId,
              boardId,
              boardOpportunityCache
            );

            if (hasExistingOpportunity) {
              opportunitiesSkipped += 1;
              continue;
            }

            try {
              const card = await postCard(
                company.id,
                listId,
                contactLabel,
                boardId,
                [contactId]
              );

              if (card?.id) {
                opportunitiesCreated += 1;
                boardOpportunityCache.set(`${contactId}:${boardId}`, true);
              } else {
                failed += 1;
              }
            } catch (error) {
              if (isDuplicateOpportunityError(error)) {
                opportunitiesSkipped += 1;
                boardOpportunityCache.set(`${contactId}:${boardId}`, true);
                continue;
              }
              throw error;
            }
          } catch (error) {
            console.error(error);
            failed += 1;
            lastErrorMessage = getContactApiErrorMessage(error);
          }
        }

        toast.dismiss(BULK_SEND_CRM_TOAST_ID);

        const summaryParts = [
          opportunitiesCreated > 0 ? `${opportunitiesCreated} oportunidad(es) creada(s)` : "",
          opportunitiesSkipped > 0
            ? `${opportunitiesSkipped} oportunidad(es) ya existían en el panel`
            : "",
          contactsCreated > 0 ? `${contactsCreated} contacto(s) creado(s)` : "",
          contactsAssigned > 0 ? `${contactsAssigned} contacto(s) asignado(s) a tu usuario` : "",
          contactsSkipped > 0 ? `${contactsSkipped} sin cambios` : "",
          failed > 0 ? `${failed} con error` : "",
        ].filter(Boolean);

        const visibleCount =
          contactsCreated + contactsAssigned + opportunitiesCreated + opportunitiesSkipped;

        if (failed > 0 && visibleCount === 0) {
          toast.error(
            `No se pudieron enviar los leads al CRM. ${summaryParts.join(". ")}.${
              lastErrorMessage ? ` Último error: ${lastErrorMessage}` : ""
            }`
          );
        } else if (visibleCount > 0) {
          toast.success(
            `Listo: ${summaryParts.join(". ")}. Puedes revisar las oportunidades en el CRM.`,
            {
              onClick: () => history.push(routes.businessBoard),
            }
          );
        } else if (contactsSkipped > 0) {
          toast.info(
            `Proceso finalizado. ${contactsSkipped} lead(s) ya estaban procesados en esta ejecución.`
          );
        } else {
          toast.warning("No se procesó ningún lead.");
        }
      } catch (error) {
        console.error(error);
        toast.dismiss(BULK_SEND_CRM_TOAST_ID);
        toast.error("No se pudo completar el envío de leads al CRM.");
      } finally {
        setIsSendingToCrm(false);
        setBulkCrmProgress(null);
      }
    },
    [
      company?.id,
      companyRole,
      extractLeadData,
      facebookForm?.form?.name,
      facebookForm?.leads,
      history,
      isAdmin,
      isReseller,
      isSendingToCrm,
      postCard,
      routes.businessBoard,
      user?.id,
    ]
  );

  const bulkProgressPercent = useMemo(() => {
    if (!bulkProgress?.total) return 0;
    return Math.round((bulkProgress.current / bulkProgress.total) * 100);
  }, [bulkProgress]);

  const exportFileBaseName = useMemo(() => {
    const safeName = (facebookForm?.form?.name || "facebook-form")
      .trim()
      .replace(/[^\w\s-áéíóúñÁÉÍÓÚÑ]/g, "")
      .replace(/\s+/g, "_");
    return `${safeName}_leads_${moment().format("DD-MM-YYYY")}`;
  }, [facebookForm?.form?.name]);

  const canExportLeads = useMemo(
    () => isAdmin || isReseller || !!companyRole?.admin,
    [companyRole?.admin, isAdmin, isReseller]
  );

  const onExportLeads = useCallback(
    (format: "csv" | "xlsx") => {
      if (!canExportLeads) {
        toast.warning(
          intl.formatMessage({ id: "app.facebookForm.exportAdminOnly" })
        );
        return;
      }

      if (!leads.length) {
        toast.info("No hay leads para exportar con los filtros actuales.");
        return;
      }

      const questions = facebookForm?.form?.questions || [];
      const rows = leads.map((lead) => {
        const { name, email, phone } = extractLeadData(lead);
        const row: Record<string, string> = {
          "ID Lead": lead.id,
          Fecha: lead.createdTime || "",
          Nombre: name,
          Email: email,
          Teléfono: phone,
        };

        questions.forEach((question, index) => {
          const label = (question || "").trim() || `Pregunta ${index + 1}`;
          row[label] = String(lead.data?.[index] || "");
        });

        return row;
      });

      const workbook = utils.book_new();
      const worksheet = utils.json_to_sheet(rows);
      utils.book_append_sheet(workbook, worksheet, "Leads");

      if (format === "csv") {
        const csvContent = utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${exportFileBaseName}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        writeFileXLSX(workbook, `${exportFileBaseName}.xlsx`);
      }

      toast.success(`Se exportaron ${rows.length} lead(s) en ${format === "csv" ? "CSV" : "Excel"}.`);
    },
    [
      canExportLeads,
      exportFileBaseName,
      extractLeadData,
      facebookForm?.form?.questions,
      intl,
      leads,
    ]
  );

  const activeLeadLoadsRef = useRef(0);
  const handleLeadLoadingChange = useCallback((loading: boolean) => {
    if (loading) {
      activeLeadLoadsRef.current += 1;
      setLeadActionLoading(true);
      return;
    }

    activeLeadLoadsRef.current = Math.max(0, activeLeadLoadsRef.current - 1);
    setLeadActionLoading(activeLeadLoadsRef.current > 0);
  }, []);

  return (
    <Layout>
      <Header2
        title={facebookForm && facebookForm.form?.name!}
        rightChildren={null}
      />
      <Content>
        <UpdateLoadingCard
          loadingId={FACEBOOK_FORM_LEADS_LOADING}
          text={intl.formatMessage({ id: "app.facebookForm.updatingAnswer" })} 
        />
        <Container fluid className={`facebook-forms-ai ${isDarkMode ? "facebook-forms-ai--dark" : ""}`}>
          {facebookForm ? (
            <>
              <div className="facebook-forms-ai__panel mb-3">
                <div className="facebook-forms-ai__panel-glow" />
                <div className="facebook-forms-ai__panel-top">
                  <div className="facebook-forms-ai__panel-head">
                    <h6 className="facebook-forms-ai__panel-title mb-0">Leads del formulario</h6>
                    <p className="facebook-forms-ai__panel-hint mb-0">
                      Gestiona respuestas y crea contactos desde esta barra.
                    </p>
                  </div>
                  <div className="facebook-forms-ai__stats">
                    <span className="facebook-forms-ai__chip">{leadStats.total} leads</span>
                    <span className="facebook-forms-ai__chip">{leadStats.questions} preguntas</span>
                  </div>
                  <div className="facebook-forms-ai__actions">
                    <div className="facebook-forms-ai__actions-main">
                      <Button color="secondary" outline className="facebook-forms-ai__pill-btn" onClick={goToFacebookForms}>
                        <i className="fas fa-arrow-left mr-1" />
                        Volver a formularios
                      </Button>
                      <Button color="secondary" outline className="facebook-forms-ai__pill-btn" onClick={toggleSideModal}>
                        Preguntas del formulario
                      </Button>
                      <Button
                        color="success"
                        className="facebook-forms-ai__pill-btn facebook-forms-ai__pill-btn--cta"
                        disabled={!leads.length}
                        title={
                          !canExportLeads
                            ? intl.formatMessage({ id: "app.facebookForm.exportAdminOnly" })
                            : undefined
                        }
                        onClick={() => onExportLeads("csv")}
                      >
                        <i className="fas fa-file-csv mr-1" />
                        Exportar CSV
                      </Button>
                      <Button
                        color="success"
                        outline
                        className="facebook-forms-ai__pill-btn"
                        disabled={!leads.length}
                        title={
                          !canExportLeads
                            ? intl.formatMessage({ id: "app.facebookForm.exportAdminOnly" })
                            : undefined
                        }
                        onClick={() => onExportLeads("xlsx")}
                      >
                        <i className="fas fa-file-excel mr-1" />
                        Exportar Excel
                      </Button>
                    </div>
                    <div className="facebook-forms-ai__actions-end">
                      <div className="facebook-forms-ai__view-selector">
                        <span className="facebook-forms-ai__view-label">Vista</span>
                        <ButtonGroup className="facebook-forms-ai__views mb-1">
                          <Button
                            color="secondary"
                            outline={viewMode !== "grid"}
                            className={`lbutton ${viewMode === "grid" ? "is-active" : ""}`}
                            onClick={() => setViewMode("grid")}
                            aria-pressed={viewMode === "grid"}
                          >
                            <i className="fas fa-table-cells mr-1" />
                            Grilla
                          </Button>
                          <Button
                            color="secondary"
                            outline={viewMode !== "cards"}
                            className={viewMode === "cards" ? "is-active" : ""}
                            onClick={() => setViewMode("cards")}
                            aria-pressed={viewMode === "cards"}
                          >
                            <i className="fas fa-id-card mr-1" />
                            Tarjetas
                          </Button>
                          <Button
                            color="secondary"
                            outline={viewMode !== "table"}
                            className={`rbutton ${viewMode === "table" ? "is-active" : ""}`}
                            onClick={() => setViewMode("table")}
                            aria-pressed={viewMode === "table"}
                          >
                            <i className="fas fa-table-list mr-1" />
                            Tabla
                          </Button>
                        </ButtonGroup>
                      </div>
                      <Button
                        color="primary"
                        outline
                        className="facebook-forms-ai__pill-btn"
                        disabled={isBulkProcessing || !facebookForm.leads?.length}
                        onClick={() => setShowCrmModal(true)}
                      >
                        {isSendingToCrm && <Spinner size="sm" className="mr-2" />}
                        {isSendingToCrm && bulkCrmProgress
                          ? `Enviando al CRM... (${bulkCrmProgress.current}/${bulkCrmProgress.total})`
                          : isSendingToCrm
                            ? "Enviando al CRM..."
                            : intl.formatMessage({ id: "app.facebookForm.sendToCrm.button" })}
                      </Button>
                      <Button
                        color="primary"
                        className="facebook-forms-ai__pill-btn facebook-forms-ai__pill-btn--cta"
                        disabled={isBulkProcessing || !facebookForm.leads?.length}
                        onClick={onCreateAllContacts}
                      >
                        {isCreatingAllContacts && <Spinner size="sm" className="mr-2" />}
                        {isCreatingAllContacts && bulkContactProgress
                          ? `Creando contactos... (${bulkContactProgress.current}/${bulkContactProgress.total})`
                          : isCreatingAllContacts
                            ? "Creando contactos..."
                            : "Crea todos los contactos"}
                      </Button>
                    </div>
                  </div>
                </div>
                {isBulkProcessing && bulkProgress ? (
                  <div className="facebook-forms-ai__progress" role="status" aria-live="polite">
                    <Spinner size="sm" />
                    <div className="facebook-forms-ai__progress-content">
                      <span className="facebook-forms-ai__progress-text">
                        {isSendingToCrm ? "Enviando al CRM" : "Procesando leads"}{" "}
                        {bulkProgress.current} de {bulkProgress.total} ({bulkProgressPercent}%)
                      </span>
                      <div className="facebook-forms-ai__progress-bar-wrap">
                        <div
                          className="facebook-forms-ai__progress-bar"
                          style={{ width: `${bulkProgressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
                <Row className="g-2">
                  <Col md="8" xs="12">
                    <InputGroup>
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar lead por nombre, mail, teléfono o contenido"
                      />
                    </InputGroup>
                  </Col>
                  <Col md="4" xs="12">
                    <Input type="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as "newest" | "oldest")}>
                      <option value="newest">Más recientes primero</option>
                      <option value="oldest">Más antiguos primero</option>
                    </Input>
                  </Col>
                </Row>
              </div>

              {leads.length === 0 ? (
                <NoData image="generic3" text="No hay leads con los filtros actuales." horizontal />
              ) : viewMode === "table" ? (
                <div className="facebook-forms-ai__table-wrap">
                  <div className="facebook-forms-ai__table-toolbar">
                    <div>
                      <h5 className="facebook-forms-ai__table-heading mb-0">Leads del formulario</h5>
                      <p className="facebook-forms-ai__table-subheading mb-0">
                        Vista detallada para revisar y abrir contactos rápidamente.
                      </p>
                    </div>
                  </div>
                  <Table responsive hover className="facebook-forms-ai__table mb-0">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        {(facebookForm.form?.questions || []).slice(0, 3).map((question, index) => (
                          <th key={`${question}-${index}`}>{question}</th>
                        ))}
                        <th>Fecha</th>
                        <th>Contacto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id}>
                          <td>
                            <div className="facebook-forms-ai__table-lead">
                              <span className="facebook-forms-ai__table-lead-title">
                                {getLeadPrimaryLabel(lead).title}
                              </span>
                              <span className="facebook-forms-ai__table-lead-subtitle">
                                {getLeadPrimaryLabel(lead).subtitle}
                              </span>
                              <code className="facebook-forms-ai__table-id">{lead.id}</code>
                            </div>
                          </td>
                          {(facebookForm.form?.questions || []).slice(0, 3).map((_, index) => (
                            <td key={`${lead.id}-${index}`}>
                              <span className="facebook-forms-ai__table-answer">
                                {lead.data?.[index] || "-"}
                              </span>
                            </td>
                          ))}
                          <td>
                            <span className="facebook-forms-ai__table-date">{lead.createdTime}</span>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              className="facebook-forms-ai__table-action"
                              disabled={!!creatingContactLeadId || isBulkProcessing}
                              onClick={() => onOpenOrCreateContact(lead)}
                            >
                              {creatingContactLeadId === lead.id && (
                                <Spinner size="sm" className="mr-1" />
                              )}
                              {creatingContactLeadId === lead.id
                                ? "Procesando..."
                                : "Abrir / crear contacto"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Row>
                  {leads.map((lead) => (
                    <Col
                      md={viewMode === "grid" ? "4" : "12"}
                      sm={viewMode === "grid" ? "6" : "12"}
                      xs="12"
                      key={lead.id}
                      className="mb-3"
                    >
                      <LeadCard
                        facebookFormLead={lead}
                        questions={facebookForm.form?.questions || []}
                        mode={
                          viewMode === "grid"
                            ? "grid"
                            : viewMode === "cards"
                              ? "cards"
                              : "list"
                        }
                        isCreatingContact={creatingContactLeadId === lead.id}
                        isContactActionDisabled={!!creatingContactLeadId || isBulkProcessing}
                        onOpenContact={() => onOpenOrCreateContact(lead)}
                        onLoadingChange={handleLeadLoadingChange}
                      />
                    </Col>
                  ))}
                </Row>
              )}
            </>
          ) : (
            <NoData
              image="generic3"
              text={intl.formatMessage({ id: "app.facebookForm.notForm" })} 
              horizontal
            />
          )}
        </Container>
      </Content>
      <SideModal
        active={isModal(FACEBOOK_VIEW_FORM)}
        visible={getModalVisibility(FACEBOOK_VIEW_FORM)}
        onClose={() => {
          turnOffModal(FACEBOOK_VIEW_FORM);
        }}
      >
        <FormQuestions questions={facebookForm.form?.questions!} />
      </SideModal>
      <SendLeadsToCrmModal
        isOpen={showCrmModal}
        leadCount={facebookForm?.leads?.length || 0}
        isSubmitting={isSendingToCrm}
        onClose={() => setShowCrmModal(false)}
        onConfirm={onSendLeadsToCrm}
      />
      <WaitingGameOffer
        isWaiting={isWaitingForContacts}
        promptLead={
          isSendingToCrm
            ? "El envío de leads al CRM está tardando un poco más de lo habitual."
            : undefined
        }
        gameNote={
          isSendingToCrm
            ? "El envío al CRM sigue en segundo plano. Puedes cerrar el juego cuando quieras."
            : undefined
        }
      />
    </Layout>
  );
}
